import { useState, useEffect } from 'react'
import { menuAPI, pagesAPI } from '../../services/api'

import { logger } from '@/utils/logger'

export default function MenuManagement() {
  const [menuItems, setMenuItems] = useState([])
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    page_id: null,
    parent_id: null,
    order_position: 0,
    is_active: true,
    open_in_new_tab: false,
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [menuResponse, pagesResponse] = await Promise.all([
        menuAPI.getAll(),
        pagesAPI.getAllAdmin()
      ])
      setMenuItems(menuResponse.data)
      setPages(pagesResponse.data)
    } catch (err) {
      setError('Fehler beim Laden der Daten')
      logger.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      // Convert empty strings to null
      const data = {
        ...formData,
        url: formData.url || null,
        page_id: formData.page_id ? parseInt(formData.page_id) : null,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        order_position: parseInt(formData.order_position),
      }

      if (editingItem) {
        await menuAPI.update(editingItem.id, data)
        setSuccess('Menü-Eintrag wurde aktualisiert')
      } else {
        await menuAPI.create(data)
        setSuccess('Menü-Eintrag wurde erstellt')
      }

      setEditingItem(null)
      setFormData({
        title: '',
        url: '',
        page_id: null,
        parent_id: null,
        order_position: 0,
        is_active: true,
        open_in_new_tab: false,
      })
      loadData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern')
      logger.error('Error saving menu item:', err)
    }
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setFormData({
      title: item.title,
      url: item.url || '',
      page_id: item.page_id || '',
      parent_id: item.parent_id || '',
      order_position: item.order_position,
      is_active: item.is_active,
      open_in_new_tab: item.open_in_new_tab,
    })
  }

  const handleDelete = async (id) => {
    if (!confirm('Möchten Sie diesen Menü-Eintrag wirklich löschen?')) return

    try {
      await menuAPI.delete(id)
      setSuccess('Menü-Eintrag wurde gelöscht')
      loadData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Löschen')
      logger.error('Error deleting menu item:', err)
    }
  }

  const cancelEdit = () => {
    setEditingItem(null)
    setFormData({
      title: '',
      url: '',
      page_id: null,
      parent_id: null,
      order_position: 0,
      is_active: true,
      open_in_new_tab: false,
    })
  }

  return (
    <div>
      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24 }}>
        {/* Form */}
        <div className="wc-card wc-elev-sm" style={{ gap: 18 }}>
          <h3 className="wc-card-title" style={{ margin: 0 }}>
            {editingItem ? 'Menü-Eintrag bearbeiten' : 'Neuer Menü-Eintrag'}
          </h3>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="wc-field">
              <label>Titel *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="wc-input"
                required
              />
            </div>

            <div className="wc-field">
              <label>CMS-Seite verknüpfen</label>
              <select
                name="page_id"
                value={formData.page_id ?? ''}
                onChange={handleChange}
                className="wc-input"
              >
                <option value="">Keine (nutze eigene URL)</option>
                {pages.map(page => (
                  <option key={page.id} value={page.id}>
                    {page.title} (/{page.slug})
                  </option>
                ))}
              </select>
              <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                Wenn eine Seite ausgewählt ist, wird die URL ignoriert
              </p>
            </div>

            <div className="wc-field">
              <label>Eigene URL (optional)</label>
              <input
                type="text"
                name="url"
                value={formData.url}
                onChange={handleChange}
                className="wc-input"
                placeholder="/apps oder https://example.com"
                disabled={!!formData.page_id}
              />
            </div>

            <div className="wc-field">
              <label>Übergeordneter Eintrag (für Dropdown)</label>
              <select
                name="parent_id"
                value={formData.parent_id ?? ''}
                onChange={handleChange}
                className="wc-input"
              >
                <option value="">Kein (Hauptmenü)</option>
                {menuItems.filter(item => !editingItem || item.id !== editingItem.id).map(item => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="wc-field">
              <label>Sortierung</label>
              <input
                type="number"
                name="order_position"
                value={formData.order_position}
                onChange={handleChange}
                className="wc-input"
              />
            </div>

            <div style={{ display: 'flex', gap: 20 }}>
              <label className="wc-check">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                Aktiv
              </label>

              <label className="wc-check">
                <input
                  type="checkbox"
                  name="open_in_new_tab"
                  checked={formData.open_in_new_tab}
                  onChange={handleChange}
                />
                In neuem Tab öffnen
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="wc-btn wc-btn-primary" style={{ flex: 1 }}>
                {editingItem ? 'Aktualisieren' : 'Erstellen'}
              </button>
              {editingItem && (
                <button type="button" onClick={cancelEdit} className="wc-btn wc-btn-secondary">
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="wc-card wc-elev-sm" style={{ gap: 14 }}>
          <h3 className="wc-card-title" style={{ margin: 0 }}>Menü-Einträge</h3>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <div className="wc-spinner" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {menuItems
                .sort((a, b) => a.order_position - b.order_position)
                .map(item => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: 12,
                      border: '1px solid var(--wc-divider)',
                      borderRadius: 'var(--wc-radius-md)',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14 }}>{item.title}</span>
                        {!item.is_active && <span className="wc-tag wc-tag-neutral">Inaktiv</span>}
                        {item.parent_id && <span className="wc-tag wc-tag-outline">Untermenü</span>}
                      </div>
                      <p className="wc-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                        {item.page_id ? `Seite ID: ${item.page_id}` : item.url || 'Keine URL'}
                      </p>
                      <p className="wc-muted" style={{ fontSize: 11, margin: '2px 0 0' }}>
                        Position: {item.order_position}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button onClick={() => handleEdit(item)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="wc-btn wc-btn-ghost"
                        style={{ padding: '4px 8px', color: '#fca5a5' }}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
